/**
 * AI Assurance 360° — metrics computation (aligned with Python CXO script).
 */

import type { FactAiAssurance360Row } from '../project-details/data-access/project.api';
import type { Project } from '@app/shared/types/project.type';

export type AssuranceProduct = 'CSA+' | 'MTC+' | 'TSC+';

export const ASSURANCE_PRODUCT_TAGS: Record<AssuranceProduct, string> = {
  'TSC+': 'TSC+',
  'CSA+': 'CSA+',
  'MTC+': 'MTC+'
};

export interface TaggedAssuranceProject {
  project: Project;
  product: AssuranceProduct;
}

export function formatAssuranceProjectLabel({ project, product }: TaggedAssuranceProject): string {
  const title = project.title || project.projectId;
  return `${title} (${ASSURANCE_PRODUCT_TAGS[product]})`;
}

export function resolveAssuranceProduct(pathname: string): AssuranceProduct {
  if (pathname.startsWith('/csa+')) return 'CSA+';
  if (pathname.startsWith('/mtc+')) return 'MTC+';
  return 'TSC+';
}

/** Restrict assurance fact rows to projects belonging to the active product. */
export function filterAssuranceRowsForProjects(
  rows: FactAiAssurance360Row[],
  projectIds: ReadonlySet<string>
): FactAiAssurance360Row[] {
  if (projectIds.size === 0) return [];
  return rows.filter(row => {
    const id = row.project_id;
    return typeof id === 'string' && id.length > 0 && projectIds.has(id);
  });
}

export const BANDS = [
  [0, 39, 'EARLY'],
  [40, 59, 'EMERGING'],
  [60, 74, 'DEFINED'],
  [75, 89, 'ADVANCED'],
  [90, 100, 'LEADING']
] as const;

export const LENS_WEIGHTS: Record<string, number> = {
  Ethics: 0.2,
  Compliance: 0.2,
  Governance: 0.25,
  Safety: 0.15,
  Security: 0.1,
  Trust: 0.1
};

export const RISK_LEVELS = ['LOW', 'MODERATE', 'HIGH'] as const;
export type RiskLevel = (typeof RISK_LEVELS)[number];

export const RISK_MAP: Record<string, RiskLevel | null> = {
  '': null,
  unknown: null,
  low: 'LOW',
  medium: 'MODERATE',
  moderate: 'MODERATE',
  under_review: 'MODERATE',
  at_risk: 'HIGH',
  high: 'HIGH',
  critical: 'HIGH'
};

const DEFAULT_TARGET_MATURITY = 80;

function clamp(x: number, lo = 0, hi = 100): number {
  return Math.max(lo, Math.min(hi, x));
}

function safeDiv(a: number, b: number): number {
  return b ? a / b : 0;
}

export function maturityBand(score: number): string {
  for (const [lo, hi, name] of BANDS) {
    if (score >= lo && score <= hi) return name;
  }
  return 'UNKNOWN';
}

function normalizeRisk(val: string | null | undefined): RiskLevel | null {
  if (val == null || val === '') return null;
  const key = String(val).trim().toLowerCase().replace(/\s+/g, '_');
  return RISK_MAP[key] ?? RISK_MAP[key.replace(/_/g, '')] ?? null;
}

export interface AiAssuranceMetrics {
  total_projects: number;
  total_reviews: number;
  approved: number;
  conditional: number;
  rejected: number;
  not_approved: number;
  dq_pct: Record<string, number>;
  lens_scores: Record<string, number | null>;
  lens_sources: Record<string, string>;
  maturity_index: number;
  maturity_level: string;
  target: number;
  target_status: string;
  confidence: string;
  rows: FactAiAssurance360Row[];
}

export function computeMetrics(
  rows: FactAiAssurance360Row[],
  targetMaturity: number = DEFAULT_TARGET_MATURITY
): AiAssuranceMetrics | null {
  if (!rows || rows.length === 0) return null;

  const projects = new Set(rows.map(r => r.project_id).filter(Boolean));
  const total_projects = projects.size;
  const total_reviews = rows.length;

  // ── Reviewer outcome counts ───────────────────────────────────────────
  let approved = 0;
  let conditional = 0;
  let rejected = 0;
  let not_approved = 0;
  for (const r of rows) {
    const s = (r.approval_status || '').toUpperCase();
    if (s === 'APPROVED') approved++;
    else if (s === 'CONDITIONAL') conditional++;
    else if (s === 'REJECTED') rejected++;
    else if (s === 'NOT_APPROVED' || s === 'NOT APPROVED') not_approved++;
  }

  // ── Data quality field coverage ───────────────────────────────────────
  const dqFields = [
    'ethical_behavior_score',
    'ai_safety_risk_pct',
    'governance_coverage_pct',
    'internal_violations_count',
    'external_violations_count',
    'owasp_fail_count',
    'ontology_overall_risk'
  ];
  const dqCounts: Record<string, number> = {};
  for (const f of dqFields) {
    dqCounts[f] = rows.filter(r => r[f] != null).length;
  }
  const dq_pct: Record<string, number> = {};
  for (const k of Object.keys(dqCounts)) {
    dq_pct[k] = Math.round(((dqCounts[k] * 100) / Math.max(1, total_reviews)) * 10) / 10;
  }

  // ── Helper: average a numeric field (null if no data) ─────────────────
  function avg(field: keyof FactAiAssurance360Row): number | null {
    const vals = rows.map(r => r[field]).filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
    if (vals.length === 0) return null;
    return vals.reduce((a, b) => a + b, 0) / vals.length;
  }

  const avg_ethics = avg('ethical_behavior_score');
  const avg_gov = avg('governance_coverage_pct');
  const avg_safety_risk = avg('ai_safety_risk_pct');

  // ── Violation & OWASP totals (null-safe) ──────────────────────────────
  const internal_viol = rows.reduce(
    (s, r) => s + (r.internal_violations_count != null ? Number(r.internal_violations_count) : 0),
    0
  );
  const external_viol = rows.reduce(
    (s, r) => s + (r.external_violations_count != null ? Number(r.external_violations_count) : 0),
    0
  );
  const owasp_fails = rows.reduce((s, r) => s + (r.owasp_fail_count != null ? Number(r.owasp_fail_count) : 0), 0);

  // ── Ethics lens ───────────────────────────────────────────────────────
  let ethics_score: number | null;
  let ethics_source: string;
  if (avg_ethics != null) {
    ethics_score = clamp(Math.round(avg_ethics));
    ethics_source = 'ethical_behavior_score';
  } else {
    const levels: RiskLevel[] = [];
    for (const r of rows) {
      for (const k of ['bias_risk_level', 'fairness_risk_level', 'explainability_risk_level'] as const) {
        const v = normalizeRisk(r[k] ?? '');
        if (v) levels.push(v);
      }
    }
    if (levels.length > 0) {
      const worst = levels.includes('HIGH') ? 'HIGH' : levels.includes('MODERATE') ? 'MODERATE' : 'LOW';
      ethics_score = ({ HIGH: 30, MODERATE: 50, LOW: 70 } as Record<string, number>)[worst] ?? null;
      ethics_source = 'ontology_fallback';
    } else {
      ethics_score = null;
      ethics_source = 'missing';
    }
  }

  // ── Governance lens ───────────────────────────────────────────────────
  const governance_score = avg_gov != null ? clamp(Math.round(avg_gov)) : null;
  const governance_source = avg_gov != null ? 'governance_coverage_pct' : 'missing';

  // ── Safety lens ───────────────────────────────────────────────────────
  let safety_score: number | null;
  let safety_source: string;
  if (avg_safety_risk != null) {
    safety_score = clamp(Math.round(100 - avg_safety_risk));
    safety_source = 'ai_safety_risk_pct';
  } else {
    const abuseLevels: RiskLevel[] = [];
    for (const r of rows) {
      const v = normalizeRisk(r.abuse_risk_level ?? '');
      if (v) abuseLevels.push(v);
    }
    if (abuseLevels.length > 0) {
      const worst = abuseLevels.includes('HIGH') ? 'HIGH' : abuseLevels.includes('MODERATE') ? 'MODERATE' : 'LOW';
      safety_score = ({ HIGH: 30, MODERATE: 55, LOW: 80 } as Record<string, number>)[worst] ?? null;
      safety_source = 'abuse_fallback';
    } else {
      safety_score = null;
      safety_source = 'missing';
    }
  }

  // ── Compliance lens ───────────────────────────────────────────────────────
  const has_violation_data = rows.some(r => r.internal_violations_count != null || r.external_violations_count != null);
  let compliance_score: number | null;
  let compliance_source: string;
  if (has_violation_data) {
    const total_viol = internal_viol + external_viol;
    if (total_viol === 0) {
      compliance_score = 100;
    } else {
      // Normalize by project count — penalize violations per project
      // Each violation per project costs 5 points, floor at 0
      const viol_per_project = total_viol / Math.max(1, total_projects);
      compliance_score = clamp(Math.round(100 - Math.min(100, viol_per_project * 5)));
    }
    compliance_source = `internal+external violations (${
      internal_viol + external_viol
    } total, ${total_projects} projects)`;
  } else {
    compliance_score = null;
    compliance_source = 'missing';
  }

  // ── Security lens ─────────────────────────────────────────────────────────
  const has_owasp_data = rows.some(r => r.owasp_fail_count != null);
  const piLevels: RiskLevel[] = [];
  for (const r of rows) {
    const v = normalizeRisk(r.prompt_injection_risk_level ?? '');
    if (v) piLevels.push(v);
  }
  let security_score: number | null;
  let security_source: string;
  if (has_owasp_data) {
    if (owasp_fails === 0) {
      security_score = 100;
    } else {
      // Normalize by review count — OWASP failures are critical, cost 20 pts each per review
      const owasp_per_review = owasp_fails / Math.max(1, total_reviews);
      security_score = clamp(Math.round(100 - Math.min(100, owasp_per_review * 20)));
    }
    security_source = `owasp_fail_count (${owasp_fails} fails across ${total_reviews} reviews)`;
  } else if (piLevels.length > 0) {
    const worst = piLevels.includes('HIGH') ? 'HIGH' : piLevels.includes('MODERATE') ? 'MODERATE' : 'LOW';
    security_score = ({ HIGH: 35, MODERATE: 60, LOW: 80 } as Record<string, number>)[worst] ?? null;
    security_source = 'prompt_injection_fallback';
  } else {
    security_score = null;
    security_source = 'missing';
  }

  // ── Trust lens ────────────────────────────────────────────────────────
  const approval_rate = safeDiv(approved, total_reviews);
  const reject_rate = safeDiv(rejected, total_reviews);
  const conditional_rate = safeDiv(conditional, total_reviews);
  const trust_score = clamp(Math.round(approval_rate * 100 - reject_rate * 60 - conditional_rate * 30));
  const trust_source = 'reviewer outcomes';

  // ── Assemble lens scores ──────────────────────────────────────────────
  const lens_scores: Record<string, number | null> = {
    Ethics: ethics_score,
    Compliance: compliance_score,
    Governance: governance_score,
    Safety: safety_score,
    Security: security_score,
    Trust: trust_score
  };
  const lens_sources: Record<string, string> = {
    Ethics: ethics_source,
    Compliance: compliance_source,
    Governance: governance_source,
    Safety: safety_source,
    Security: security_source,
    Trust: trust_source
  };

  // ── Maturity index: weighted average of AVAILABLE lenses only ─────────
  // null lenses excluded — weights renormalized across available lenses
  const available = Object.fromEntries(Object.entries(lens_scores).filter(([, v]) => v != null)) as Record<
    string,
    number
  >;

  let maturity_index: number;
  let confidence: string;

  if (Object.keys(available).length === 0) {
    maturity_index = 0;
    confidence = 'LOW';
  } else {
    const weightSum = Object.keys(available).reduce((s, k) => s + (LENS_WEIGHTS[k] ?? 0), 0);
    maturity_index = clamp(
      Math.round(Object.entries(available).reduce((sum, [k, v]) => sum + ((LENS_WEIGHTS[k] ?? 0) / weightSum) * v, 0))
    );
    const evidenceRatio =
      Object.values(dqCounts).reduce((a, b) => a + b, 0) / Math.max(1, total_reviews * dqFields.length);
    if (total_projects <= 1 || evidenceRatio < 0.35) confidence = 'LOW';
    else if (evidenceRatio < 0.75) confidence = 'MEDIUM';
    else confidence = 'HIGH';
  }
  console.log('total_viol:', internal_viol + external_viol);
  console.log('owasp_fails:', owasp_fails);
  console.log('compliance_score AFTER fix:', compliance_score);
  console.log('security_score AFTER fix:', security_score);
  return {
    total_projects,
    total_reviews,
    approved,
    conditional,
    rejected,
    not_approved,
    dq_pct,
    lens_scores,
    lens_sources,
    maturity_index,
    maturity_level: maturityBand(maturity_index),
    target: targetMaturity,
    target_status: maturity_index >= targetMaturity ? 'MEETS TARGET' : 'BELOW TARGET',
    confidence,
    rows
  };
}

export function getHeatmapMatrix(rows: FactAiAssurance360Row[]): {
  dims: [string, string][];
  mat: number[][];
} {
  const dims: [string, string][] = [
    ['Bias', 'bias_risk_level'],
    ['Fairness', 'fairness_risk_level'],
    ['Explainability', 'explainability_risk_level'],
    ['Abuse', 'abuse_risk_level'],
    ['Prompt Injection', 'prompt_injection_risk_level'],
    ['Security', 'security_risk_level']
  ];
  const mat = dims.map(() => [0, 0, 0]);
  for (const r of rows) {
    for (let i = 0; i < dims.length; i++) {
      const col = dims[i]![1];
      const val = r[col as keyof FactAiAssurance360Row];
      const lvl = normalizeRisk(typeof val === 'string' ? val : null);
      if (lvl && RISK_LEVELS.includes(lvl)) {
        const j = RISK_LEVELS.indexOf(lvl);
        mat[i]![j]++;
      }
    }
  }
  return { dims, mat };
}

export function getGovernanceTrendByDate(rows: FactAiAssurance360Row[]): {
  dates: string[];
  approved: number[];
  conditional: number[];
  rejected: number[];
  not_approved: number[];
} {
  const bucket: Record<string, { APPROVED: number; CONDITIONAL: number; REJECTED: number; NOT_APPROVED: number }> = {};

  for (const r of rows) {
    const dt = r.review_timestamp_utc;
    if (!dt) continue;
    const dateStr = typeof dt === 'string' ? dt.split('T')[0]! : String(dt).split('T')[0]!;
    if (!bucket[dateStr]) {
      bucket[dateStr] = { APPROVED: 0, CONDITIONAL: 0, REJECTED: 0, NOT_APPROVED: 0 };
    }
    const raw = (r.approval_status || 'APPROVED').toUpperCase().replace(/\s+/g, '_');
    const status = raw === 'NOT_APPROVED' ? 'NOT_APPROVED' : raw;
    if (status in bucket[dateStr]) {
      bucket[dateStr][status as keyof (typeof bucket)[string]]++;
    }
  }

  const dates = Object.keys(bucket).sort();
  return {
    dates,
    approved: dates.map(d => bucket[d]!.APPROVED),
    conditional: dates.map(d => bucket[d]!.CONDITIONAL),
    rejected: dates.map(d => bucket[d]!.REJECTED),
    not_approved: dates.map(d => bucket[d]!.NOT_APPROVED)
  };
}

// ── Risk Score derived from Radar/Spider graph lens scores ────────────
// Risk is the INVERSE of maturity — where the polygon DIPS = high risk
// Each lens gets a risk weight based on regulatory/business impact
export const RISK_WEIGHTS: Record<string, number> = {
  Ethics: 0.25, // highest — reputational & regulatory
  Governance: 0.25, // highest — audit & accountability
  Compliance: 0.2, // legal exposure
  Safety: 0.15, // operational risk
  Security: 0.1, // technical risk
  Trust: 0.05 // outcome-based
};

export type RiskLevel2 = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | 'MINIMAL';

export interface RiskPosture {
  overall_risk_score: number; // 0–100 (higher = more risk)
  overall_risk_level: RiskLevel2;
  lens_risks: Record<string, number | null>; // per-lens risk score
  weakest_lens: string; // lens with highest risk
  strongest_lens: string; // lens with lowest risk
  risk_gap: number; // weakest - strongest (spread)
  recommendation: string; // actionable summary
}

export function riskBand(score: number): RiskLevel2 {
  if (score >= 80) return 'CRITICAL';
  if (score >= 60) return 'HIGH';
  if (score >= 40) return 'MEDIUM';
  if (score >= 20) return 'LOW';
  return 'MINIMAL';
}

export function computeRiskPosture(metrics: AiAssuranceMetrics): RiskPosture {
  // Invert each lens score → risk score per lens
  // null lens (no data) = treated as HIGH RISK (unknown = risky)
  const lens_risks: Record<string, number | null> = {};
  for (const [lens, score] of Object.entries(metrics.lens_scores)) {
    lens_risks[lens] = score != null ? clamp(100 - score) : null;
  }

  // Available lenses with data for weighted average
  const available = Object.entries(lens_risks).filter(([, v]) => v != null) as [string, number][];

  let overall_risk_score = 0;
  if (available.length > 0) {
    const weightSum = available.reduce((s, [k]) => s + (RISK_WEIGHTS[k] ?? 0), 0);
    overall_risk_score = clamp(
      Math.round(available.reduce((sum, [k, v]) => sum + ((RISK_WEIGHTS[k] ?? 0) / weightSum) * v, 0))
    );
  }

  // Find weakest (highest risk) and strongest (lowest risk) lens
  const sorted = available.sort(([, a], [, b]) => b - a);
  const weakest_lens = sorted[0]?.[0] ?? 'N/A';
  const strongest_lens = sorted[sorted.length - 1]?.[0] ?? 'N/A';
  const risk_gap = (sorted[0]?.[1] ?? 0) - (sorted[sorted.length - 1]?.[1] ?? 0);

  // Actionable recommendation based on weakest lens
  const recommendations: Record<string, string> = {
    Ethics: 'Improve bias detection, fairness scores and explainability coverage',
    Governance: 'Strengthen governance framework and review coverage percentage',
    Compliance: 'Reduce internal/external policy violations immediately',
    Safety: 'Lower AI safety risk percentage through model hardening',
    Security: 'Address OWASP failures and prompt injection vulnerabilities',
    Trust: 'Increase reviewer approval rate and reduce rejections'
  };

  const recommendation =
    weakest_lens !== 'N/A'
      ? `Focus area: ${weakest_lens} — ${recommendations[weakest_lens] ?? 'Review this lens'}`
      : 'Insufficient data to generate recommendation';

  return {
    overall_risk_score,
    overall_risk_level: riskBand(overall_risk_score),
    lens_risks,
    weakest_lens,
    strongest_lens,
    risk_gap,
    recommendation
  };
}
